import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, ChevronDown, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import './Plans.css';
import { fadeInUp } from '../constants/animations';
import { buildApiUrl } from '../utils/api';

const STRIPE_JS_SRC = 'https://js.stripe.com/v3/';
const STRIPE_PUBLISHABLE_KEY =
  'pk_live_51S18B22R7owCIULXyRlRjdv7FZRBN4Cx0N2ZEUmbbEggOEcphDh9eS1YRoybiOcxMwI6iZ0I24mIGGMy2dNATuST00dcpsfM2V';

const loadStripeJs = () =>
  new Promise((resolve, reject) => {
    if (window.Stripe) return resolve(window.Stripe);
    const existing = document.querySelector(`script[src="${STRIPE_JS_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Stripe));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = STRIPE_JS_SRC;
    script.async = true;
    script.onload = () => resolve(window.Stripe);
    script.onerror = reject;
    document.body.appendChild(script);
  });

function Plans() {
  const [openFaq, setOpenFaq] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutReady, setCheckoutReady] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  const checkoutMountRef = useRef(null);
  const checkoutInstanceRef = useRef(null);
  const ctaButtonRef = useRef(null);
  const checkoutSectionRef = useRef(null);
  const reduceMotion = useReducedMotion();

  const tearDownCheckout = useCallback(() => {
    if (checkoutInstanceRef.current) {
      try { checkoutInstanceRef.current.destroy(); } catch (_) {}
      checkoutInstanceRef.current = null;
    }
    setCheckoutReady(false);
    setCheckoutError(null);
  }, []);

  useEffect(() => () => tearDownCheckout(), [tearDownCheckout]);

  useEffect(() => {
    if (!checkoutOpen) return undefined;
    let cancelled = false;
    setCheckoutError(null);
    setCheckoutReady(false);

    (async () => {
      try {
        const Stripe = await loadStripeJs();
        if (cancelled) return;
        const stripe = Stripe(STRIPE_PUBLISHABLE_KEY);
        const instance = await stripe.initEmbeddedCheckout({
          fetchClientSecret: async () => {
            const response = await fetch(buildApiUrl('/api/create-checkout-session'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' }
            });
            if (!response.ok) throw new Error('Unable to start checkout.');
            const { clientSecret } = await response.json();
            return clientSecret;
          }
        });
        if (cancelled) {
          try { instance.destroy(); } catch (_) {}
          return;
        }
        if (!checkoutMountRef.current) {
          try { instance.destroy(); } catch (_) {}
          return;
        }
        instance.mount(checkoutMountRef.current);
        checkoutInstanceRef.current = instance;
        setCheckoutReady(true);
      } catch (err) {
        if (!cancelled) {
          setCheckoutError('Checkout is temporarily unavailable. Please refresh or contact us.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [checkoutOpen]);

  const handleStartCheckout = useCallback(() => {
    if (typeof window.gtagSendEvent === 'function') {
      try { window.gtagSendEvent(); } catch (_) {}
    }
    setCheckoutOpen(true);
    requestAnimationFrame(() => {
      const node = checkoutSectionRef.current;
      if (node && typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'start'
        });
      }
    });
  }, [reduceMotion]);

  const handleCloseCheckout = useCallback(() => {
    tearDownCheckout();
    setCheckoutOpen(false);
    requestAnimationFrame(() => {
      if (ctaButtonRef.current && typeof ctaButtonRef.current.focus === 'function') {
        ctaButtonRef.current.focus();
      }
    });
  }, [tearDownCheckout]);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqItems = [
    {
      q: 'How fast can you launch?',
      a: 'Most businesses are live in 7 days. We handle the setup.'
    },
    {
      q: 'No contract?',
      a: 'Nope. Cancel anytime. We only stay if we\'re delivering results.'
    },
    {
      q: 'What if I need changes?',
      a: 'We\'ve got you. Our team is available for updates and support.'
    },
    {
      q: 'Do you offer custom work?',
      a: 'Absolutely. Multi-location setups, integrations, custom features - we build it.'
    }
  ];

  const revealTransition = reduceMotion
    ? { duration: 0 }
    : { duration: 0.45, ease: [0.16, 1, 0.3, 1] };

  return (
    <main className="pricing-page">
      <motion.section
        className="pricing-hero"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.25 }}
      >
        <div className="container">
          <motion.span className="pricing-kicker" variants={fadeInUp} custom={0}>Pricing</motion.span>
          <motion.h1 variants={fadeInUp} custom={1}>Simple pricing your team can <span className="plans-gold-text">trust</span>.</motion.h1>
          <motion.p variants={fadeInUp} custom={2}>One plan. All features. Month‑to‑month.</motion.p>
        </div>
      </motion.section>

      <motion.section
        className="pricing-main"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="container">
          <div
            ref={checkoutSectionRef}
            className={`pricing-shell ${checkoutOpen ? 'pricing-shell--checkout-open' : ''}`}
          >
            <motion.div
              className="pricing-card"
              variants={fadeInUp}
              custom={0}
            >
              <span className="plan-pill">All‑in‑One</span>

              <AnimatePresence initial={false}>
                {!checkoutOpen && (
                  <motion.div
                    key="plan-headline"
                    className="plan-headline"
                    initial={false}
                    exit={reduceMotion
                      ? { opacity: 0 }
                      : { opacity: 0, height: 0, marginTop: 0, marginBottom: 0 }}
                    transition={{ duration: reduceMotion ? 0 : 0.28, ease: [0.16, 1, 0.3, 1] }}
                    style={{ overflow: 'hidden' }}
                  >
                    <h2>$299 <span>/month</span></h2>
                    <p className="plan-note">Everything included. Cancel anytime.</p>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="pricing-actions">
                <AnimatePresence mode="wait" initial={false}>
                  {!checkoutOpen ? (
                    <motion.button
                      key="cta"
                      ref={ctaButtonRef}
                      type="button"
                      className="primary-btn primary-btn--cta"
                      onClick={handleStartCheckout}
                      aria-expanded={checkoutOpen}
                      aria-controls="embedded-checkout-region"
                      initial={reduceMotion ? false : { opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
                      transition={{ duration: reduceMotion ? 0 : 0.18 }}
                    >
                      Get Started
                    </motion.button>
                  ) : (
                    <motion.button
                      key="back"
                      type="button"
                      className="checkout-back-btn"
                      onClick={handleCloseCheckout}
                      initial={reduceMotion ? false : { opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: reduceMotion ? 0 : 0.18 }}
                    >
                      <ArrowLeft size={16} aria-hidden="true" />
                      <span>Back to plan</span>
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>

              <AnimatePresence initial={false}>
                {checkoutOpen && (
                  <motion.div
                    key="checkout-region"
                    id="embedded-checkout-region"
                    role="region"
                    aria-label="Secure checkout"
                    className="checkout-region"
                    initial={reduceMotion ? { opacity: 1, height: 'auto' } : { opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={reduceMotion ? { opacity: 0, height: 0 } : { opacity: 0, height: 0 }}
                    transition={revealTransition}
                  >
                    <div className="checkout-region-inner">
                      {!checkoutReady && !checkoutError && (
                        <div className="checkout-loading" aria-live="polite">
                          <Loader2 size={20} className="checkout-spinner" aria-hidden="true" />
                          <span>Securing your checkout…</span>
                        </div>
                      )}
                      {checkoutError && (
                        <div className="checkout-error-state" role="alert">
                          <p>{checkoutError}</p>
                          <button
                            type="button"
                            className="primary-btn primary-btn--cta"
                            onClick={() => {
                              tearDownCheckout();
                              setCheckoutOpen(false);
                              requestAnimationFrame(() => setCheckoutOpen(true));
                            }}
                          >
                            Try again
                          </button>
                        </div>
                      )}
                      <div
                        ref={checkoutMountRef}
                        className={`checkout-mount ${checkoutReady ? 'checkout-mount--visible' : ''}`}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="mini-points">
                <span>24/7 support</span>
                <span>7‑day launch</span>
                <span>No lock‑in</span>
              </div>
            </motion.div>

            <motion.div
              className="comfort-panel"
              variants={fadeInUp}
              custom={1}
            >
              <h3>What you get</h3>
              <ul>
                <li>Beautiful custom website</li>
                <li>Local search optimization</li>
                <li>AI phone assistant (24/7)</li>
                <li>Business phone number</li>
                <li>Lead forms & booking</li>
                <li>Email & SMS marketing</li>
                <li>Online reviews management</li>
                <li>Expert setup + support</li>
              </ul>
              <Link to="/features" className="explore-link">
                Explore all features →
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.section>

      <motion.section
        className="pricing-assurance"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="container">
          <div className="assurance-grid">
            <motion.div className="assurance-card" variants={fadeInUp} custom={0}>
              <h4>Month‑to‑month</h4>
              <p>No contracts. Stay because it works.</p>
            </motion.div>
            <motion.div className="assurance-card" variants={fadeInUp} custom={1}>
              <h4>White‑glove setup</h4>
              <p>We build it for you, fast.</p>
            </motion.div>
            <motion.div className="assurance-card" variants={fadeInUp} custom={2}>
              <h4>No hidden fees</h4>
              <p>One flat rate, everything included.</p>
            </motion.div>
          </div>
        </div>
      </motion.section>

      <motion.section
        className="pricing-faq"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="container">
          <h3>Questions<span className="plans-gold-text">?</span></h3>
          <div className="faq-accordion">
            {faqItems.map((item, idx) => (
              <div
                key={idx}
                className={`faq-item ${openFaq === idx ? 'open' : ''}`}
              >
                <button
                  className="faq-question"
                  onClick={() => toggleFaq(idx)}
                >
                  <span>{item.q}</span>
                  <ChevronDown size={20} />
                </button>
                <div className="faq-answer">
                  <p>{item.a}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="faq-bottom-cta">
            <h4>Still have questions?</h4>
            <a href="mailto:hello@versalaunch.com" className="talk-to-sales-btn">
              Talk to Sales
            </a>
          </div>
        </div>
      </motion.section>
    </main>
  );
}

export default Plans;
